import React from 'react';
import { useField, useFormikContext } from 'formik';
import styled from 'styled-components';
import { useToast } from '../toast/ToastContext';
import StyledImageUpload from '../StyledComponents/StyledImageUpload';

interface UploadedFile {
    file: File;
    name: string;
    type: string;
    rename: string;
    progress: number;
}

interface FormikFileUploadProps {
    name: string;
    multiple?: boolean;
    accept?: string;
    icon?: React.ReactNode;
    showRenameInput?: boolean;
    onChange?: (files: UploadedFile[]) => void; // 👈 Required for preview
    setHasUploaded: (state: boolean) => void;
    checkError?: string;
    isResponsive?: boolean;
    previewUrl?: string;
    uploadProgress?: number;
    isUploading?: boolean;
    removeFile?: () => void;
    existingFileName?: string;
    existingFileSize?: number;
}

const FormikImageUpload: React.FC<FormikFileUploadProps> = ({
    name,
    multiple = false,
    accept = "*/*",
    icon,
    showRenameInput,
    onChange,
    setHasUploaded,
    checkError,
    isResponsive,
    previewUrl,
    uploadProgress,
    isUploading,
    removeFile,
    existingFileName,
    existingFileSize,

}) => {
    const [field, meta] = useField(name);
    const { setFieldValue } = useFormikContext();
    const showToast = useToast();

    // This handler is now optional — parent can override
    const internalHandleChange = (files: UploadedFile[]) => {
        if (onChange) {
            onChange(files); // Delegate to parent
            return;
        }

        // Fallback (not used in our case)
        if (!multiple && files?.length > 1) {
            showToast("Upload Error", 'Only one image can be uploaded at a time.', "error");
            return;
        }
        // ... fallback logic if needed
    };

    return (
        <>
            <StyledImageUpload
                showRenameInput={showRenameInput}
                accept={accept}
                icon={icon}
                multiple={multiple}
                onChange={internalHandleChange}
                removeFile={removeFile}
                isResponsive={isResponsive}
                previewUrl={previewUrl}
                uploadProgress={uploadProgress}   // 👈
                isUploading={isUploading}         // 👈
                existingFileName={existingFileName}
                existingFileSize={existingFileSize}
            />
            {meta.touched && meta.error && (
                <ErrorText>{meta.error as string}</ErrorText>
            )}
            {checkError ? <ErrorText>{checkError}</ErrorText> : null}
        </>
    );
};

export default FormikImageUpload;

const ErrorText = styled.div`
  color: #d32f2f;
  font-size: 13px;
  margin-top: 6px;
`;